import { MMTP_PACKET_ID_MAX, MMTP_SIGNALING_PACKET_ID, admittedMulticastEndpoints } from "@blockcast/mmt-base";
import type {
	WindowMulticastSubscribeConfig,
	WindowMulticastSubscribeConfigBytes,
} from "./types.js";

export type MulticastTransportPreference = "auto" | "native" | "tunnel";

export { DEFAULT_AMT_RELAY_PORT } from "@blockcast/mmt-base";

export interface MulticastEndpointConfig {
	groupAddress: string;
	sourceAddress?: string;
	/** ASM sender filter (`auth.senderAddress` of a protected ASM endpoint); never beside `sourceAddress`. */
	senderAddress?: string;
	port: number;
	mode?: MulticastTransportPreference;
	amtRelay?: string;
	amtRelayPort?: number;
	/** `"driad"`: with no relay, the gateway discovers one per RFC 8777. */
	amtDiscovery?: "driad";
}

/**
 * Input accepted by the canonical window.multicast request converter.
 *
 * `mode` and the `amtRelay*` fields are internal endpoint-selection state;
 * they are deliberately not emitted on the page-facing request.  The
 * resulting `origin` is the only acquisition selector understood by the
 * gateway.
 */
export interface WindowMulticastEndpointInput {
	group: string;
	port: number;
	source?: string;
	/** ASM sender filter; emitted as the request's `senderAddress`. Refused beside `source`. */
	senderAddress?: string;
	mode?: MulticastTransportPreference;
	amtRelay?: string;
	amtRelayPort?: number;
	/** `"driad"`: with no relay, the gateway discovers one per RFC 8777. */
	amtDiscovery?: "driad";
	packetIds?: readonly number[];
	tracks?: ReadonlyArray<{ name: string; packetId: number }>;
	/**
	 * ATSC 3.0 tuning from the catalog's `atsc3` networkSource
	 * (`catalogAtsc3NetworkSource`). Draft -01 3 puts the tuner first: when set,
	 * the request is the tuner member and relay/mode state is not consulted.
	 */
	atsc3?: { frequency: number; plpId?: number; serviceId?: number };
}

/**
 * Convert internal endpoint-selection state to the canonical page contract.
 *
 * Native is the default and an explicit `native` selection always wins over
 * stale relay metadata.  Tunnel/AMT requests must carry the resolved relay
 * and port; there is no implicit relay-port default at this boundary.
 *
 * That rule is deliberate and is NOT the place to fix a relay that names no
 * port: this is the last hop before the page-facing request, so a default
 * applied here would be invisible to every caller and to the join it produces.
 * A catalog relay with no port gets `DEFAULT_AMT_RELAY_PORT` upstream, in the
 * readers that resolve it, and arrives here explicit like any other
 * (BLO-38176).
 */
export function toWindowMulticastSubscribeConfig(
	input: WindowMulticastEndpointInput,
): WindowMulticastSubscribeConfig {
	if (input.mode !== undefined && !["auto", "native", "tunnel"].includes(input.mode)) {
		throw new TypeError(`Unsupported multicast mode: ${String(input.mode)}`);
	}
	// An explicit non-array (the null "endpoint unusable" sentinel from
	// selectEndpointPacketIdsForTracks) must not degrade into "no filter".
	if (input.packetIds !== undefined && !Array.isArray(input.packetIds)) {
		throw new TypeError(
			`multicast packetIds must be an array, got ${input.packetIds === null ? "null" : typeof input.packetIds}`,
		);
	}
	// Every join request passes through here, so the track packet_id range is
	// enforced here rather than trusted from each caller.
	if (input.packetIds?.some(id => !isMulticastTrackPacketId(id)) ||
		input.tracks?.some(track => !isMulticastTrackPacketId(track.packetId))) {
		throw new RangeError(
			`multicast packetId must be an integer from 1 to ${MMTP_PACKET_ID_MAX}; ` +
			"packetId 0 is the MMTP signaling flow and is never a track's",
		);
	}

	const source = input.source?.trim() || undefined;
	const senderAddress = input.senderAddress?.trim() || undefined;
	// Draft -01 7.2.6: S is sourceAddress on SSM and senderAddress on ASM, never both.
	if (source && senderAddress) {
		throw new TypeError("multicast senderAddress is an ASM filter and never appears beside source");
	}
	const base = {
		group: input.group,
		port: input.port,
		...(source ? { source } : {}),
		consume: "bytes" as const,
		...(input.packetIds?.length ? { packetIds: [...input.packetIds] } : {}),
	};
	const filter = senderAddress ? { senderAddress } : {};

	if (input.atsc3) {
		// The contract's tuner member carries no sender filter.
		if (senderAddress) throw new TypeError("an ATSC3 tuner request carries no senderAddress");
		const { frequency, plpId, serviceId } = input.atsc3;
		if (!Number.isInteger(frequency) || frequency < 1) {
			throw new TypeError(`ATSC3 tuning requires a positive integer frequency in kHz, got ${String(frequency)}`);
		}
		return {
			...base,
			origin: "tuner",
			standard: "atsc3",
			frequency,
			...(plpId !== undefined ? { plpId } : {}),
			...(serviceId !== undefined ? { serviceId } : {}),
		};
	}

	const relay = input.amtRelay?.trim();
	// A relay wins over DRIAD: it is a commitment to that relay.
	const driad = !relay && input.amtDiscovery === "driad";
	const wantsAmt = input.mode === "tunnel" ||
		(input.mode !== "native" && ((relay !== undefined && relay !== "") || driad));
	if (!wantsAmt) {
		const native: WindowMulticastSubscribeConfigBytes = {
			...base,
			...filter,
			origin: "ip-multicast",
			...(input.tracks?.length ? { tracks: input.tracks.map(({ name, packetId }) => ({ name, packetId })) } : {}),
		};
		return native;
	}

	const relayPort = input.amtRelayPort;
	if (driad) {
		if (!base.source) throw new TypeError("AMT DRIAD discovery requires a source address");
		if (relayPort !== undefined && !isPositivePort(relayPort)) {
			throw new TypeError("AMT DRIAD discovery requires a valid relay port when one is given");
		}
		return {
			...base,
			source: base.source,
			origin: "amt",
			discovery: "driad",
			...(relayPort !== undefined ? { relayPort } : {}),
		};
	}
	if (!relay || relayPort === undefined || !isPositivePort(relayPort)) {
		throw new TypeError("AMT requires a resolved relay and a valid relay port");
	}
	return {
		...base,
		...filter,
		origin: "amt",
		relay,
		relayPort,
	};
}

export function isValidMulticastGroupAddress(address: string | undefined): boolean {
	const value = address?.trim().toLowerCase();
	if (!value) return false;
	if (value.startsWith("ff")) return isValidIpv6MulticastAddress(value);

	const parts = value.split(".");
	if (parts.length !== 4) return false;
	const octets = parts.map((part) => {
		if (!/^\d+$/.test(part)) return Number.NaN;
		const octet = Number(part);
		return Number.isInteger(octet) && octet >= 0 && octet <= 255 ? octet : Number.NaN;
	});
	return octets.every(Number.isInteger) && octets[0] >= 224 && octets[0] <= 239;
}

export function normalizeMulticastEndpoint(
	endpoint: MulticastEndpointConfig,
): MulticastEndpointConfig | undefined {
	const groupAddress = endpoint.groupAddress.trim().toLowerCase();
	const sourceAddress = endpoint.sourceAddress?.trim().toLowerCase() || undefined;
	const senderAddress = endpoint.senderAddress?.trim().toLowerCase() || undefined;
	if (sourceAddress && senderAddress) return undefined;
	const amtRelay = endpoint.amtRelay?.trim().toLowerCase() || undefined;
	if (!isValidMulticastGroupAddress(groupAddress) || !isPositivePort(endpoint.port)) return undefined;
	if (endpoint.mode && !["auto", "native", "tunnel"].includes(endpoint.mode)) return undefined;
	if (endpoint.amtRelayPort !== undefined && !isPositivePort(endpoint.amtRelayPort)) return undefined;

	return {
		groupAddress,
		...(sourceAddress ? { sourceAddress } : {}),
		...(senderAddress ? { senderAddress } : {}),
		port: endpoint.port,
		...(endpoint.mode ? { mode: endpoint.mode } : {}),
		...(amtRelay ? { amtRelay } : {}),
		...(endpoint.amtRelayPort ? { amtRelayPort: endpoint.amtRelayPort } : {}),
		...(endpoint.amtDiscovery === "driad" ? { amtDiscovery: "driad" as const } : {}),
	};
}

/** Stable ownership key for one IWA multicast/AMT subscription. */
export function multicastEndpointKey(endpoint: MulticastEndpointConfig): string {
	const normalized = normalizeMulticastEndpoint(endpoint);
	if (!normalized) throw new Error("Invalid multicast endpoint");
	return JSON.stringify([
		normalized.sourceAddress ?? "",
		normalized.groupAddress,
		normalized.port,
		normalized.mode ?? "auto",
		normalized.amtRelay ?? "",
		normalized.amtRelayPort ?? "",
		// A DRIAD join is a different gateway subscription from a native one.
		...(normalized.amtDiscovery ? [normalized.amtDiscovery] : []),
		// An ASM sender filter is a different subscription from the unfiltered join.
		...(normalized.senderAddress ? [{ senderAddress: normalized.senderAddress }] : []),
	]);
}

/**
 * True when `value` may be advertised as a multicast track's MMTP `packet_id`:
 * an integer in 1..MMTP_PACKET_ID_MAX. packet_id 0 (MMTP_SIGNALING_PACKET_ID)
 * is the MMTP signaling flow -- the FFmpeg `moq_mmt` muxer sends the AL-FEC
 * message (0x0203) on it into the same group as the media -- so it is never a
 * track's. Only a number qualifies: a string, boolean or null is not coerced.
 */
export function isMulticastTrackPacketId(value: unknown): value is number {
	return Number.isInteger(value) &&
		(value as number) > MMTP_SIGNALING_PACKET_ID &&
		(value as number) <= MMTP_PACKET_ID_MAX;
}

/**
 * True when a receiver may join `endpoint`: a multicast group, a usable port
 * and, given the `catalog` it came from, admission under draft -01 4.1 / 7.2.6
 * (`admittedUnderCatalog`). Without a catalog object only the first two are checked.
 */
export function isUsableMulticastEndpoint(
	endpoint: { groupAddress?: string; port?: unknown } | undefined,
	catalog?: unknown,
): boolean {
	const port = Number(endpoint?.port);
	return isValidMulticastGroupAddress(endpoint?.groupAddress) && isPositivePort(port) &&
		// Not a catalog (an `Array#filter` index, say): checks the first two only.
		(catalog === null || typeof catalog !== "object" || admittedUnderCatalog(catalog, endpoint));
}

/**
 * Whether one endpoint survives the draft -01 receiver rules in the context
 * of `catalog`: its mode and `S` agree, it carries `auth` exactly when the
 * catalog's `multicast` object does, and that catalog holds a key set. The
 * rules are `multicastCatalogIssues` of `@blockcast/mmt-base`, the same the
 * container validator throws on. Cross-endpoint rules need the whole array;
 * `admittedEndpointsUnderCatalog` applies those.
 */
export function admittedUnderCatalog(catalog: unknown, endpoint: unknown): boolean {
	return admittedEndpointsUnderCatalog(catalog, [endpoint]).has(0);
}

/**
 * Indices of `endpoints` that survive every draft -01 receiver rule as the
 * endpoint array of `catalog`, cross-endpoint rules included: both sides of a
 * shared `(S, G, port)` or a clashing `manifestPort` are dropped.
 */
export function admittedEndpointsUnderCatalog(catalog: unknown, endpoints: readonly unknown[]): ReadonlySet<number> {
	const root = catalog !== null && typeof catalog === "object" ? (catalog as Record<string, unknown>) : {};
	const multicast = root["multicast"] !== null && typeof root["multicast"] === "object"
		? (root["multicast"] as Record<string, unknown>)
		: {};
	return admittedMulticastEndpoints({ ...root, multicast: { ...multicast, endpoints } });
}

/**
 * Unprefixed aliases of the `mc_` URL override keys, retired in BLO-37507, each
 * paired with the key that replaces it.
 */
const RETIRED_MULTICAST_URL_PARAMS = [
	["amt_relay", "mc_amt_relay"],
	["amt_relay_port", "mc_amt_relay_port"],
] as const;

/**
 * Reject a URL multicast override that still carries a retired unprefixed alias.
 *
 * Both URL readers call this only once a valid override is established (a valid
 * multicast `mc_group` and a valid `mc_port`; `readUrlMulticastConfig` also
 * requires the whole endpoint to normalize), which is where they used to read
 * the aliases: a link that
 * relied on one fails here with its replacement named, instead of silently
 * losing its AMT relay (and, in `readUrlMulticastConfig`, its tunnel mode).
 * Presence is the error, whatever the value and whether or not the `mc_` form
 * is also present: quietly preferring one of two keys is the tolerant read being
 * retired. Outside an override the readers read no override key at all, the
 * `mc_` forms included, so the bare names are left to the page; they are live
 * page parameters elsewhere, and keeping clear of those is what the `mc_`
 * namespace is for.
 */
export function assertNoRetiredMulticastUrlParams(params: URLSearchParams): void {
	for (const [retired, replacement] of RETIRED_MULTICAST_URL_PARAMS) {
		if (params.has(retired)) {
			throw new Error(
				`multicast URL override: query parameter "${retired}" is retired; use "${replacement}"`,
			);
		}
	}
}

/**
 * Read an explicit multicast endpoint override from `mc_*` URL parameters.
 * Throws when an override carries a retired unprefixed alias
 * (`assertNoRetiredMulticastUrlParams`).
 */
export function readUrlMulticastConfig(
	search: string | URLSearchParams = typeof location === "undefined" ? "" : location.search,
): MulticastEndpointConfig | undefined {
	const params = typeof search === "string" ? new URLSearchParams(search) : search;
	const groupAddress = params.get("mc_group") || "";
	const port = positivePort(params.get("mc_port"));
	if (!groupAddress || !port) return undefined;

	const amtRelay = params.get("mc_amt_relay") || undefined;
	const amtRelayPort = positivePort(params.get("mc_amt_relay_port"));
	const requestedMode = params.get("mc_mode");
	const mode = amtRelay ? "tunnel" : multicastMode(requestedMode);

	const endpoint = normalizeMulticastEndpoint({
		groupAddress,
		sourceAddress: params.get("mc_source") || undefined,
		port,
		...(mode ? { mode } : {}),
		...(amtRelay ? { amtRelay } : {}),
		...(amtRelayPort ? { amtRelayPort } : {}),
	});
	// An endpoint that does not normalize (e.g. a non-multicast mc_group) is no
	// override, so the page keeps its own bare names; the same scope
	// readPlayerMulticastConfig applies by validating the group first.
	if (endpoint) assertNoRetiredMulticastUrlParams(params);
	return endpoint;
}

export function isMulticastExplicitlyDisabled(
	search: string | URLSearchParams = typeof location === "undefined" ? "" : location.search,
): boolean {
	const params = typeof search === "string" ? new URLSearchParams(search) : search;
	const multicast = params.get("multicast")?.trim().toLowerCase();
	const transport = params.get("transport")?.trim().toLowerCase();
	return transport === "moq-only" || ["0", "false", "no", "off"].includes(multicast || "");
}

function isValidIpv6MulticastAddress(value: string): boolean {
	if (!/^[0-9a-f:]+$/.test(value)) return false;
	const compressed = value.includes("::");
	if (value.indexOf("::") !== value.lastIndexOf("::")) return false;
	const groups = value.split(":").filter(Boolean);
	if (groups.length === 0 || groups.length > 8) return false;
	if (!compressed && groups.length !== 8) return false;
	return groups.every((group) => /^[0-9a-f]{1,4}$/.test(group));
}

function multicastMode(value: string | null): MulticastTransportPreference | undefined {
	return value === "native" || value === "tunnel" || value === "auto" ? value : undefined;
}

function positivePort(value: string | null): number | undefined {
	const port = Number(value);
	return isPositivePort(port) ? port : undefined;
}

function isPositivePort(port: number): boolean {
	return Number.isInteger(port) && port > 0 && port <= 65535;
}
